/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { injectIntl, FormattedMessage } from 'react-intl';

import Dropdown from '../form/Dropdown';
import Input from '../form/Input';
import PasswordInput from '../form/PasswordInput';
import Checkbox from '../form/Checkbox';
import BottomBar from '../BottomBar';
import { titles, toMultiSelectOptions } from '../helpers';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { checkEmail } from '../../../actions/misc';
import { createUser, fetchUser } from '../../../actions/user';
import {
  updatePersonalDetails,
  updatePracticePreferences,
} from '../../../actions/preferences';

const PersonalDetails = ({
  currentStep,
  intl,
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  specialized,
  draftValues,
  onDraftChange,
}) => {
  const dispatch = useDispatch();
  const emailError = useSelector(state => state.misc.emailError);
  const locale = _.get(
    useSelector(state => state.misc),
    'appLocale',
    'en',
  );
  const pronouns = _.get(
    useSelector(state => state.misc),
    'pronouns',
    [],
  );

  const pronounOptions = toMultiSelectOptions(
    getUniquePronounOptions(pronouns, locale),
  );

  const { control, handleSubmit, watch, getValues } = useForm({
    defaultValues: draftValues || {
      title: '',
      first_name: '',
      last_name: '',
      pronouns: [],
      pronoun_other_yn: false,
      pronoun_other_name: '',
      email: '',
      confirm_email: '',
      password: '',
      confirm_password: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const isPronounOtherVisible = watch('pronoun_other_yn')?.length > 0;

  const onSubmit = async values => {
    const errorMessage = (
      <FormattedMessage defaultMessage="There was a problem creating your account." />
    );

    const personalDetailValues = {
      personal_detail: { title: _.get(values, 'title.value') },
    };

    const pronounValues = {
      pronoun: {
        pronouns: values.pronouns,
        pronoun_other_yn: values.pronoun_other_yn,
        pronoun_other_name: values.pronoun_other_name,
      },
    };

    try {
      openLoadingPanel(
        <FormattedMessage defaultMessage="Creating account..." />,
      );

      await dispatch(
        createUser({
          ...values,
          locale: localStorage.getItem('locale') || 'en',
          role_type: specialized ? 'generic' : 'counsellor',
          source: 'practice',
        }),
      );

      await dispatch(
        fetchUser(() => {
          dispatch(updatePersonalDetails(personalDetailValues));
          dispatch(updatePracticePreferences(pronounValues));
          closeLoadingPanel();
          nextPage();
        }),
      );
    } catch (err) {
      errorCallback(err, errorMessage);
    }
  };

  // async email validation
  const checkEmailDebounced = _.debounce(value => {
    if (value) dispatch(checkEmail(value));
  }, 300);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title" style={{ marginBottom: '20px' }}>
        <h1>
          <FormattedMessage defaultMessage="Personal Details" />
        </h1>
      </div>
      <h4 style={{ marginBottom: '40px' }}>
        <FormattedMessage defaultMessage="Only your name will be visible to your clients" />
      </h4>

      {/* Title, First & Last Name */}
      <div className="input-row">
        <Controller
          name="title"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState: { error } }) => (
            <Dropdown
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              options={titles}
              label={intl.formatMessage({ defaultMessage: 'Title' })}
              error={error?.message}
            />
          )}
        />
        <Controller
          name="first_name"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              label={intl.formatMessage({
                defaultMessage: 'First Name',
              })}
              error={error?.message}
            />
          )}
        />
        <Controller
          name="last_name"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              label={intl.formatMessage({ defaultMessage: 'Last Name' })}
              error={error?.message}
            />
          )}
        />
      </div>

      {/* Pronouns */}
      <div className="input-row">
        <Controller
          name="pronouns"
          control={control}
          render={({ field }) => (
            <Dropdown
              {...field}
              multi
              label={intl.formatMessage({ defaultMessage: 'Pronouns' })}
              options={pronounOptions}
            />
          )}
        />
        <Controller
          name="pronoun_other_yn"
          control={control}
          render={({ field }) => (
            <Checkbox
              {...field}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({ defaultMessage: 'Other' }),
                },
              ]}
              style={{ marginTop: 40 }}
            />
          )}
        />
        {isPronounOtherVisible && (
          <Controller
            name="pronoun_other_name"
            control={control}
            rules={{
              required: intl.formatMessage({ defaultMessage: 'Required' }),
            }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                // eslint-disable-next-line react/jsx-no-literals, formatjs/no-literal-string-in-jsx
                label={<span>&zwnj;</span>}
                error={error?.message}
              />
            )}
          />
        )}
      </div>

      {/* Email */}
      <div className="input-row">
        <Controller
          name="email"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
            pattern: {
              value: /^[À-ÿA-Z0-9._%+-]+@[À-ÿA-Z0-9.-]+\.[A-Z]{2,6}$/i,
              message: intl.formatMessage({
                defaultMessage: 'Invalid email address',
              }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              label={intl.formatMessage({ defaultMessage: 'Email' })}
              error={emailError || error?.message}
              onChange={e => {
                field.onChange(e);
                checkEmailDebounced(e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="confirm_email"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
            validate: value =>
              value === watch('email') ||
              intl.formatMessage({ defaultMessage: 'Emails do not match' }),
          }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              required
              label={intl.formatMessage({
                defaultMessage: 'Confirm Email',
              })}
              error={error?.message}
            />
          )}
        />
      </div>

      {/* Password */}
      <div className="input-row">
        <Controller
          name="password"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters',
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <PasswordInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              label={`${intl.formatMessage({ defaultMessage: 'Password' })} *`}
              error={error?.message}
            />
          )}
        />
        <Controller
          name="confirm_password"
          control={control}
          rules={{
            required: intl.formatMessage({ defaultMessage: 'Required' }),
            validate: value =>
              value === watch('password') || 'Passwords do not match',
          }}
          render={({ field, fieldState: { error } }) => (
            <PasswordInput
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              label={intl.formatMessage({
                defaultMessage: 'Confirm Password',
              })}
              error={error?.message}
            />
          )}
        />
      </div>

      {/* Bottom Bar */}
      <BottomBar
        specialized={specialized}
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        currentStep={currentStep}
        nextPage={nextPage}
      />
    </form>
  );
};

PersonalDetails.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  intl: PropTypes.object,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
  specialized: PropTypes.bool,
};

const getUniquePronounOptions = (pronouns, locale) => {
  const result = [];
  const names = new Set();
  pronouns.forEach(pronoun => {
    if (!pronoun.other_yn) {
      const name = pronoun.name_i18n?.[locale];
      if (name && !names.has(name)) {
        result.push({ id: pronoun.id, name });
        names.add(name);
      }
    }
  });
  return result;
};

export default injectIntl(PersonalDetails);
